// "Use this template" — name (prefilled), start date and time zone (browser
// default). Emits the POST /programs/from-template body; the caller runs the
// mutation and navigates to the new draft.
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import type { FromTemplateInput, ProgramTemplate } from '@/services/learningService';
import { browserTimezone, localDayOf } from '../lib/format';

interface Props {
  template: ProgramTemplate | null;
  onClose: () => void;
  onConfirm: (body: FromTemplateInput) => void;
  busy?: boolean;
}

export const InstantiateDialog: React.FC<Props> = ({ template, onClose, onConfirm, busy }) => {
  const t = useT();
  return (
    <Dialog open={!!template} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('training:tpl_use_title', 'New program from template')}</DialogTitle>
          <DialogDescription>{t('training:tpl_use_help', 'A draft program is created with the template\'s sessions placed from the start date in this time zone. You then set the audience and publish.')}</DialogDescription>
        </DialogHeader>
        {template && <InstantiateForm key={template.id} template={template} onClose={onClose} onConfirm={onConfirm} busy={busy} />}
      </DialogContent>
    </Dialog>
  );
};

const InstantiateForm: React.FC<Props & { template: ProgramTemplate }> = ({ template, onClose, onConfirm, busy }) => {
  const t = useT();
  const [name, setName] = useState(template.name);
  const [startsOn, setStartsOn] = useState(localDayOf(new Date().toISOString()));
  const [timezone, setTimezone] = useState(browserTimezone());
  const ok = !!startsOn && !!timezone.trim();
  return (
    <div className="space-y-3" data-testid="instantiate-dialog">
      <div className="space-y-1">
        <Label htmlFor="inst-name" className="text-xs">{t('training:wiz_name', 'Program name')}</Label>
        <Input id="inst-name" className="h-9" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="inst-start" className="text-xs">{t('training:tpl_starts_on', 'Start date')}</Label>
          <Input id="inst-start" className="h-9" type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} disabled={busy} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inst-tz" className="text-xs">{t('training:wiz_timezone', 'Time zone')}</Label>
          <Input id="inst-tz" className="h-9" value={timezone} onChange={(e) => setTimezone(e.target.value)} disabled={busy} />
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">{t('training:tpl_sessions_from', '{{n}} session(s) will be scheduled from this date.', { n: (template.definition?.sessions || []).length })}</p>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>{t('training:cancel', 'Cancel')}</Button>
        <Button size="sm" disabled={!ok || busy} data-testid="instantiate-confirm"
          onClick={() => onConfirm({ template_id: template.id, name: name.trim() || undefined, starts_on: startsOn, timezone: timezone.trim() })}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:tpl_create_draft', 'Create draft')}
        </Button>
      </div>
    </div>
  );
};

export default InstantiateDialog;
